import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useAddCategorieMutation,
  useUpdateCategorieMutation,
} from '@spartalabs/pdc-core';
import { SimpleInput } from 'components';
import { useCustomToast } from 'hooks';
import * as z from 'zod';

interface CancelModalProps {
  isOpen: boolean;
  onClose: (isCancel?: boolean) => void;
  categorie?: Categorie | null;
}

const CatSchema = z.object({
  title: z.string().nonempty({ message: 'Campo obrigatório' }),
  desc: z.string().nonempty({ message: 'Campo obrigatório' }),
});

type CatSchemaProps = z.infer<typeof CatSchema>;

export const CategorieModal = ({
  isOpen,
  onClose,
  categorie,
}: CancelModalProps) => {
  const [addFaq, { isLoading: createLoading }] = useAddCategorieMutation();
  const [editFaq, { isLoading: editLoading }] = useUpdateCategorieMutation();

  const { handleToast } = useCustomToast();

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<CatSchemaProps>({
    resolver: zodResolver(CatSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onSubmit = useCallback(
    async ({ title, desc }: CatSchemaProps) => {
      try {
        if (categorie) {
          await editFaq({ ...categorie, title, desc });
        } else {
          await addFaq({ title, desc });
        }
        onClose();
      } catch {
        handleToast({
          step: 4,
          action: `${categorie ? 'editar' : 'criar'} a categoria`,
          helpText: 'Não é possível realizar essa ação',
        });
      }
    },
    [categorie],
  );

  useEffect(() => {
    if (categorie) setValue('title', categorie?.title || '');
  }, [categorie]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent w={{ base: '340px', md: '468px' }}>
        <ModalBody mt="10px">
          <Box w="full" display="flex" flexDir="column">
            <Text mb="12px" color="text.100" fontSize="xl">
              {categorie ? 'Editar categoria' : 'Adicionar categoria'}
            </Text>

            <SimpleInput
              textColor="text.100"
              placeholder="Digite o nome da categoria"
              labelColor="text.100"
              label="Nome da categoria"
              aria-labelledby="label"
              data-cy="title-input"
              maxLength={60}
              borderColor="grey.600"
              error={errors.title?.message}
              {...register('title')}
            />

            <Stack mt="16px">
              <Text
                aria-hidden="true"
                as="label"
                fontSize="14px"
                color="text.100"
              >
                Descrição sobre a categoria
              </Text>
              <>
                <Textarea
                  textColor="text.100"
                  placeholder="Digite uma descrição sobre da categoria"
                  aria-labelledby="label"
                  data-cy="title-input"
                  maxLength={120}
                  borderColor="grey.600"
                  {...register('desc')}
                />
                {errors?.desc && (
                  <Text
                    alignSelf="start"
                    pl="8px"
                    color="red.200"
                    fontSize="sm"
                  >
                    {errors.desc.message}
                  </Text>
                )}
              </>
            </Stack>
          </Box>

          <ModalCloseButton color="black" />
        </ModalBody>

        <ModalFooter justifyContent="center">
          <Button variant="outline" mr={3} onClick={() => onClose()}>
            Voltar
          </Button>
          <Button
            isLoading={createLoading || editLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {categorie ? 'Editar' : 'Criar'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
